// Delegated data-action router.
//
// Tumaini 2.0 renders NO inline onclick handlers (CSP-hostile and the source
// of the legacy jsq() breakout class). Instead, interactive elements carry
// `data-action="name"` (+ optional data-* args) and ONE document-level click
// listener dispatches to handlers registered here.
//
// Keyboard semantics are ported from the legacy document keydown handler
// (v20 line ~1134): Enter/Space activate any click-only element opted in via
// [data-kbd-click], EXCEPT when focus is in a form control (INPUT/TEXTAREA/
// SELECT) or contentEditable — typing must never trigger card clicks.

/** @type {Map<string, (el: Element, ev: Event) => void>} */
const handlers = new Map();

/**
 * Register a named action handler. Re-registering a name replaces it.
 * @param {string} name
 * @param {(el: Element, ev: Event) => void} fn receives the [data-action] element
 */
export function register(name, fn) {
  handlers.set(name, fn);
}

/** Test-only: clear all registered handlers. @returns {void} */
export function _resetActions() {
  handlers.clear();
}

// ─── Pure dispatch logic (unit-tested without a DOM) ───────────────────────

/**
 * Resolve which registered action a click on `el` should fire.
 * Walks up via el.closest('[data-action]') — the element model only needs
 * .closest and .getAttribute, so tests can pass minimal fakes.
 * @param {any} target event target (Element-like)
 * @returns {{name: string, el: any}|null}
 */
export function resolveAction(target) {
  if (!target || typeof target.closest !== 'function') return null;
  const el = target.closest('[data-action]');
  if (!el) return null;
  const name = el.getAttribute('data-action');
  if (!name || !handlers.has(name)) return null;
  return { name, el };
}

/**
 * Legacy [data-kbd-click] gate as a pure predicate.
 *
 * NATIVELY-ACTIVATABLE TARGETS ARE EXCLUDED. A real <button> or <a href> fires
 * its own click on Enter/Space; if we preventDefault and click() the ANCESTOR
 * carrying [data-kbd-click] instead, the inner control becomes unreachable by
 * keyboard and the wrong action runs. (Board cards hit exactly this: tabbing to
 * "Set stage" and pressing Enter opened the patient workspace.) The browser's
 * own activation is always correct for these, so we stand aside.
 *
 * @param {string} key event.key
 * @param {{tagName?: string, isContentEditable?: boolean, hasKbdClick: boolean, isNativelyActivatable?: boolean}|null} target
 * @returns {boolean} true when the event should preventDefault + click()
 */
export function kbdActivates(key, target) {
  if (key !== 'Enter' && key !== ' ') return false;
  if (!target || !target.hasKbdClick) return false;
  if (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName || '')) return false;
  if (target.isContentEditable) return false;
  if (target.isNativelyActivatable) return false;
  return true;
}

/**
 * True when the element activates itself on Enter/Space without our help — a
 * <button>, or an <a> with an href. Pure; only needs .tagName/.getAttribute so
 * tests can pass minimal fakes.
 * @param {any} el
 * @returns {boolean}
 */
export function isNativelyActivatable(el) {
  if (!el) return false;
  const tag = String(el.tagName || '').toUpperCase();
  if (tag === 'BUTTON') return true;
  if (tag === 'A' && typeof el.getAttribute === 'function' && el.getAttribute('href') !== null) return true;
  return false;
}

// ─── DOM wiring (thin) ─────────────────────────────────────────────────────

/**
 * Install the two document-level listeners. Idempotent per document.
 * @param {Document} doc
 * @returns {void}
 */
export function installActions(doc) {
  const d = /** @type {any} */ (doc);
  if (d._tumainiActionsInstalled) return;
  d._tumainiActionsInstalled = true;

  doc.addEventListener('click', (ev) => {
    const hit = resolveAction(/** @type {any} */ (ev.target));
    if (!hit) return;
    const fn = handlers.get(hit.name);
    if (fn) fn(hit.el, ev);
  });

  doc.addEventListener('keydown', (ev) => {
    const t = /** @type {any} */ (ev.target);
    const kb = t && typeof t.closest === 'function' ? t.closest('[data-kbd-click]') : null;
    const should = kbdActivates(ev.key, {
      tagName: t && t.tagName,
      isContentEditable: !!(t && t.isContentEditable),
      hasKbdClick: !!kb,
      isNativelyActivatable: isNativelyActivatable(t),
    });
    if (!should) return;
    ev.preventDefault();
    kb.click(); // routes through the click listener above when it also has data-action
  });
}
